<script setup lang="ts">
import '@/style/style-sentry-feedback.css';
import '@/style/style-shepherd.css';
import 'shepherd.js/dist/css/shepherd.css';

import { computed, onMounted, watch } from 'vue';
import { RouterView, useRoute, useRouter } from 'vue-router';

import SidebarLayout from '@/components/sidebar/SidebarLayout.vue';
import Toaster from '@/components/ui/toast/Toaster.vue';
import { flushAttribution } from '@/services/attribution';
import { useAccessContextStore } from '@/stores/accessContextStore';
import { useAuthStore } from '@/stores/auth';
import { useTenantStore } from '@/stores/tenant';
import { useThemeStore } from '@/stores/theme';

const accessContext = useAccessContextStore();
const auth = useAuthStore();
const tenant = useTenantStore();
const theme = useThemeStore();
const router = useRouter();
const route = useRoute();

const isAccessPending = computed(() => {
    return (
        auth.isAuthenticated && !isPublicRoute.value && !accessContext.loaded
    );
});

const canShowAppShell = computed(() => {
    return (
        auth.isAuthenticated &&
        !isPublicRoute.value &&
        accessContext.loaded &&
        accessContext.hasActiveSubscription &&
        accessContext.hasCompletedOnboarding
    );
});

const isPublicRoute = computed(() => Boolean(route.meta.public));

onMounted(async () => {
    theme.initializeTheme();
    await auth.handleAuth();
});

/* Send a pending acquisition attribution as soon as a tenant is known. Not awaited.
A failed call leaves the attribution in memory, so it is retried if the tenant changes,
but a reload drops it: nothing is persisted on the device. */
watch(
    () => [auth.isAuthenticated, tenant.tenantId] as const,
    ([isAuthenticated, tenantId]) => {
        if (isAuthenticated && tenantId) void flushAttribution(tenantId);
    },
    { immediate: true }
);

watch(
    () => [auth.isLoading, auth.isAuthenticated, isPublicRoute.value] as const,
    ([isLoading, isAuthenticated, isPublicRoute]) => {
        if (
            auth.isDescopeEnabled &&
            !isLoading &&
            isAuthenticated &&
            isPublicRoute
        ) {
            router.replace('/');
        }
    },
    { immediate: true }
);
</script>

<template>
    <Toaster />
    <div
        v-if="auth.isLoading || isAccessPending"
        class="flex text-foreground bg-background h-screen"
    />
    <div
        v-else-if="canShowAppShell"
        class="h-screen p-1 bg-primary30 dark:bg-darkerBackground"
    >
        <SidebarLayout>
            <RouterView />
        </SidebarLayout>
    </div>
    <RouterView v-else-if="auth.isDescopeEnabled || auth.isAuthenticated" />
</template>
